import { ReactNode } from "react";

interface HoverLiftProps {
  children: ReactNode;
  className?: string;
  amount?: number;
}

// CSS-only hover lift to prevent forced reflows
export function HoverLift({ children, className = "", amount = -4 }: HoverLiftProps) {
  return (
    <div
      className={`transition-transform duration-200 ease-out hover:translate-y-[var(--lift-amount)] ${className}`}
      style={{ "--lift-amount": `${amount}px` } as React.CSSProperties}
    >
      {children}
    </div>
  );
}

interface HoverScaleProps {
  children: ReactNode;
  className?: string;
  scale?: number;
}

// CSS-only hover scale
export function HoverScale({ children, className = "", scale = 1.02 }: HoverScaleProps) {
  return (
    <div
      className={`transition-transform duration-200 ease-out hover:scale-[var(--scale-amount)] active:scale-[0.98] ${className}`}
      style={{ "--scale-amount": scale } as React.CSSProperties}
    >
      {children}
    </div>
  );
}

interface HoverGlowProps {
  children: ReactNode;
  className?: string;
}

// CSS-only hover glow
export function HoverGlow({ children, className = "" }: HoverGlowProps) {
  return (
    <div className={`${className} relative group`}>
      <div className="absolute inset-0 rounded-2xl bg-accent/20 blur-xl opacity-0 scale-[0.8] group-hover:opacity-100 group-hover:scale-110 transition-all duration-300" />
      <div className="relative">{children}</div>
    </div>
  );
}

interface TapBounceProps {
  children: ReactNode;
  className?: string;
}

// CSS-only tap bounce
export function TapBounce({ children, className = "" }: TapBounceProps) {
  return (
    <div className={`transition-transform duration-200 ease-out active:scale-95 ${className}`}>
      {children}
    </div>
  );
}

interface PulseOnHoverProps {
  children: ReactNode;
  className?: string;
}

// CSS-only pulse on hover using keyframes
export function PulseOnHover({ children, className = "" }: PulseOnHoverProps) {
  return (
    <div className={`hover:animate-pulse-subtle ${className}`}>
      {children}
    </div>
  );
}

interface ShakeOnErrorProps {
  children: ReactNode;
  className?: string;
  shake?: boolean;
}

// CSS-only shake animation
export function ShakeOnError({ children, className = "", shake = false }: ShakeOnErrorProps) {
  return (
    <div className={`${shake ? "animate-shake" : ""} ${className}`}>
      {children}
    </div>
  );
}

interface RotateOnHoverProps {
  children: ReactNode;
  className?: string;
  degrees?: number;
}

// CSS-only rotate on hover
export function RotateOnHover({ children, className = "", degrees = 5 }: RotateOnHoverProps) {
  return (
    <div
      className={`transition-transform duration-200 ease-out hover:rotate-[var(--rotate-deg)] ${className}`}
      style={{ "--rotate-deg": `${degrees}deg` } as React.CSSProperties}
    >
      {children}
    </div>
  );
}

interface MagneticProps {
  children: ReactNode;
  className?: string;
}

// CSS-only magnetic effect
export function Magnetic({ children, className = "" }: MagneticProps) {
  return (
    <div className={`transition-transform duration-200 ease-out hover:scale-110 active:scale-90 ${className}`}>
      {children}
    </div>
  );
}

interface FadeInViewProps {
  children: ReactNode;
  className?: string;
  delay?: number;
}

// CSS-only fade in (uses intersection observer via animate-fade-up class)
export function FadeInView({ children, className = "", delay = 0 }: FadeInViewProps) {
  return (
    <div
      className={`animate-fade-up ${className}`}
      style={{ animationDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
}

interface RippleButtonProps {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}

// CSS-only ripple button with hover/active states
export function RippleButton({ children, className = "", onClick }: RippleButtonProps) {
  return (
    <button
      className={`${className} relative overflow-hidden transition-transform duration-200 ease-out hover:scale-[1.02] active:scale-[0.98]`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
